export default class DepositCalculator {
    static calculate(product, startDate) {
        console.log('=== Deposit Calculator Started ===');
        console.log('Product Data:', JSON.stringify(product, null, 2));
        console.log('Start Date:', startDate);

        // Validate required fields
        if (!product.depositType) {
            throw new Error('depositType is required');
        }
        if (!product.installmentAmount || product.installmentAmount <= 0) {
            throw new Error(`installmentAmount must be greater than 0, got: ${product.installmentAmount}`);
        }
        if (!product.term || product.term <= 0) {
            throw new Error(`term must be greater than 0, got: ${product.term}`);
        }
        if (!product.interestRate || product.interestRate <= 0) {
            throw new Error(`interestRate must be greater than 0, got: ${product.interestRate}`);
        }

        const p = {
            depositType: product.depositType,
            frequency: product.frequency || 'Monthly',
            installmentAmount: product.installmentAmount,
            term: product.term,
            termType: product.termType || 'Month',
            interestRate: product.interestRate,
            interestType: (product.interestType || 'Compound').trim(),
            compounding: product.compounding || 'Monthly'
        };

        console.log('Mapped Product:', p);

        let result;
        switch (p.depositType.toUpperCase()) {
            case "FD":
                result = this.calculateFD(p, startDate);
                break;
            case "RD":
                result = this.calculateRD(p, startDate);
                break;
            case "MIS":
                result = this.calculateMIS(p, startDate);
                break;
            case "DDS":
                result = this.calculateDDS(p, startDate);
                break;
            default:
                throw new Error(`Invalid Product: ${p.depositType}`);
        }
        
        console.log('Final Result:', result);
        console.log('=== Deposit Calculator Finished ===');
        return result;
    }

    //----------------------------------------------------
    // Fixed Deposit
    //----------------------------------------------------
    static calculateFD(p, start) {
        console.log('Calculating FD...');
        let maturity = this.addPeriod(start, p.term, p.termType);
        let years = this.daysBetween(start, maturity) / 365;
        let interest = this.calculateInterest(
            p.installmentAmount,
            p.interestRate,
            years,
            p.interestType,
            p.compounding
        );

        return {
            maturityDate: maturity,
            principal: p.installmentAmount,
            interest: Number(interest.toFixed(2)),
            maturityAmount: Number((p.installmentAmount + interest).toFixed(2))
        };
    }

    //----------------------------------------------------
    // Recurring Deposit
    //----------------------------------------------------
    static calculateRD(p, start) {
        console.log('Calculating RD...');
        console.log(`Number of installments: ${p.term}, Frequency: ${p.frequency}`);
        console.log(`Installment Amount: ${p.installmentAmount}`);
        console.log(`Interest Rate: ${p.interestRate}%`);
        console.log(`Interest Type: ${p.interestType}`);
        console.log(`Compounding: ${p.compounding}`);

        // Calculate maturity date based on frequency and number of installments
        let maturity = this.addInstallmentPeriod(start, p.term, p.frequency);
        console.log(`Maturity Date calculated: ${maturity.toISOString().split('T')[0]}`);
        
        let totalDeposit = 0;
        let totalInterest = 0;
        let installmentDate = new Date(start);
        
        for (let i = 0; i < p.term; i++) {
            let years = this.daysBetween(installmentDate, maturity) / 365;
            let interest = this.calculateInterest(
                p.installmentAmount,
                p.interestRate,
                years,
                p.interestType,
                p.compounding
            );
            
            totalInterest += interest;
            totalDeposit += p.installmentAmount;
            
            if (i < 3 || i === p.term - 1) {
                console.log(`Installment ${i + 1}: Date=${installmentDate.toISOString().split('T')[0]}, Interest=${interest.toFixed(4)}`);
            }
            
            installmentDate = this.nextInstallmentDate(
                installmentDate,
                p.frequency
            );
        }

        console.log(`Total Deposit: ${totalDeposit}`);
        console.log(`Total Interest: ${totalInterest.toFixed(2)}`);
        console.log(`Maturity Amount: ${(totalDeposit + totalInterest).toFixed(2)}`);

        return {
            maturityDate: maturity,
            principal: totalDeposit,
            interest: Number(totalInterest.toFixed(2)),
            maturityAmount: Number((totalDeposit + totalInterest).toFixed(2))
        };
    }

    //----------------------------------------------------
    // MIS
    //----------------------------------------------------
    static calculateMIS(p, start) {
        console.log('Calculating MIS...');
        let maturity = this.addPeriod(start, p.term, 'Month');
        let years = this.daysBetween(start, maturity) / 365;
        let yearlyInterest = p.installmentAmount * p.interestRate / 100;
        let monthlyIncome = yearlyInterest / 12;
        
        return {
            maturityDate: maturity,
            principal: p.installmentAmount,
            monthlyIncome: Number(monthlyIncome.toFixed(2)),
            maturityAmount: p.installmentAmount
        };
    }

    //----------------------------------------------------
    // Daily Deposit Scheme
    //----------------------------------------------------
    static calculateDDS(p, start) {
        console.log('Calculating DDS...');
        return this.calculateRD(p, start);
    }

    //----------------------------------------------------
    // Interest
    //----------------------------------------------------
    static calculateInterest(principal, rate, years, type, compounding) {
        if (type?.toUpperCase() === "SIMPLE") {
            return principal * rate * years / 100;
        }
        
        let n = {
            Daily: 365,
            Monthly: 12,
            Quarterly: 4,
            HalfYearly: 2,
            Yearly: 1
        }[compounding] || 1;
        
        return principal * (Math.pow(1 + rate / 100 / n, n * years) - 1);
    }

    //----------------------------------------------------
    // Date Helpers
    //----------------------------------------------------
    static nextInstallmentDate(date, frequency) {
        let d = new Date(date);
        switch (frequency) {
            case "Daily":
                d.setDate(d.getDate() + 1);
                break;
            case "Weekly":
                d.setDate(d.getDate() + 7);
                break;
            case "BiWeekly":
                d.setDate(d.getDate() + 14);
                break;
            case "Monthly":
                d.setMonth(d.getMonth() + 1);
                break;
            case "Quarterly":
                d.setMonth(d.getMonth() + 3);
                break;
            case "Half-Yearly":
                d.setMonth(d.getMonth() + 6);
                break;
            case "Yearly":
                d.setFullYear(d.getFullYear() + 1);
                break;
            default:
                d.setMonth(d.getMonth() + 1);
                break;
        }
        return d;
    }

    static addInstallmentPeriod(startDate, numberOfInstallments, frequency) {
        let d = new Date(startDate);
        console.log(`Adding ${numberOfInstallments} installments with ${frequency} frequency`);
        
        switch (frequency) {
            case "Daily":
                d.setDate(d.getDate() + numberOfInstallments);
                break;
            case "Weekly":
                d.setDate(d.getDate() + (numberOfInstallments * 7));
                break;
            case "BiWeekly":
                d.setDate(d.getDate() + (numberOfInstallments * 14));
                break;
            case "Monthly":
                d.setMonth(d.getMonth() + numberOfInstallments);
                break;
            case "Quarterly":
                d.setMonth(d.getMonth() + (numberOfInstallments * 3));
                break;
            case "Half-Yearly":
                d.setMonth(d.getMonth() + (numberOfInstallments * 6));
                break;
            case "Yearly":
                d.setFullYear(d.getFullYear() + numberOfInstallments);
                break;
            default:
                d.setMonth(d.getMonth() + numberOfInstallments);
                break;
        }
        
        console.log(`Maturity date: ${d.toISOString().split('T')[0]}`);
        return d;
    }

    static addPeriod(date, term, type) {
        let d = new Date(date);
        switch (type) {
            case "Day":
                d.setDate(d.getDate() + term);
                break;
            case "Week":
                d.setDate(d.getDate() + term * 7);
                break;
            case "Month":
                d.setMonth(d.getMonth() + term);
                break;
            case "Year":
                d.setFullYear(d.getFullYear() + term);
                break;
            default:
                d.setMonth(d.getMonth() + term);
                break;
        }
        return d;
    }

    static daysBetween(d1, d2) {
        const diff = (d2 - d1) / (1000 * 60 * 60 * 24);
        console.log(`Days between: ${d1.toISOString().split('T')[0]} and ${d2.toISOString().split('T')[0]} = ${diff} days`);
        return diff;
    }
}
